import { BRAND_KIT_CAPTION_POSITIONS } from '../brandKit/types';
import type { BrandKitCaptionPosition, BrandKitCaptionStyleFields, BrandKitFields } from '../brandKit/types';
import type {
  CaptionAnimation,
  CaptionFont,
  CaptionPosition,
  CaptionSize,
  LogoPosition,
  LogoSize,
  CaptionCapabilities,
  CaptionCapabilitiesResponse,
  CaptionStyle,
  CaptionStyleResponse,
} from './types';

export const LOGO_POSITIONS: LogoPosition[] = ['top_left', 'top_right', 'bottom_left', 'bottom_right'];
export const LOGO_SIZES: LogoSize[] = ['small', 'medium', 'large'];
export const CAPTION_POSITIONS: CaptionPosition[] = ['top', 'bottom', 'lower-third', 'middle'];
export const CAPTION_SIZES: CaptionSize[] = ['small', 'medium', 'large'];
export const CAPTION_FONTS: CaptionFont[] = ['display', 'sans', 'condensed', 'mono'];
export const CAPTION_ANIMATIONS: CaptionAnimation[] = ['pop', 'rise', 'highlight'];

const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/;

/** The contract's defaults before the Brand Kit is applied: font `sans`, position `bottom`, PPS's colours. */
export const DEFAULT_CAPTION_STYLE: CaptionStyle = {
  logo: { enabled: false, url: null, position: 'top_right', size: 'medium' },
  captions: {
    font: 'sans',
    color: null,
    highlightColor: null,
    backgroundColor: null,
    size: 'medium',
    position: 'bottom',
    animation: null,
    uppercase: false,
  },
};

/** What PPS's renderer draws today (STUDIO_API.md), used until the server says otherwise. */
export const DEFAULT_CAPTION_CAPABILITIES: CaptionCapabilities = {
  captions: {
    fonts: [...CAPTION_FONTS],
    animations: [...CAPTION_ANIMATIONS],
    positions: ['bottom', 'lower-third', 'middle'],
    background: false,
    layoutAlwaysDrawn: false,
    uppercaseAnimations: ['pop'],
    timingEditable: true,
  },
  logo: { placement: 'end_card', position: false, size: false },
  tags: { effect: 'rerank' },
};

/** The colour PPS uses when a style leaves one null, for the preview and the colour fields. */
export const PPS_DEFAULT_COLORS = { color: '#FFFFFF', highlightColor: '#F5B700', backgroundColor: '#000000' } as const;

const oneOf = <T extends string>(allowed: T[], value: unknown, fallback: T): T =>
  allowed.includes(value as T) ? (value as T) : fallback;

const colorOrNull = (value: unknown, fallback: string | null): string | null => {
  if (value === null) return null;
  return typeof value === 'string' && HEX_COLOR.test(value) ? value.toUpperCase() : fallback;
};

const animationOr = (value: unknown, fallback: CaptionAnimation | null): CaptionAnimation | null => {
  if (value === null) return null;
  return CAPTION_ANIMATIONS.includes(value as CaptionAnimation) ? (value as CaptionAnimation) : fallback;
};

/** Tolerant decode: a missing or unknown value falls back to `fallback`, field by field. */
export function mapCaptionStyle(
  data: CaptionStyleResponse | null | undefined,
  fallback: CaptionStyle = DEFAULT_CAPTION_STYLE,
  logoUrl: string | null = fallback.logo.url
): CaptionStyle {
  const logo = data?.logo ?? {};
  const captions = data?.captions ?? {};
  const base = fallback.captions;
  return {
    logo: {
      enabled: typeof logo.enabled === 'boolean' ? logo.enabled : fallback.logo.enabled,
      url: logoUrl,
      position: oneOf(LOGO_POSITIONS, logo.position, fallback.logo.position),
      size: oneOf(LOGO_SIZES, logo.size, fallback.logo.size),
    },
    captions: {
      font: oneOf(CAPTION_FONTS, captions.font, base.font),
      color: colorOrNull(captions.color, base.color),
      highlightColor: colorOrNull(captions.highlightColor, base.highlightColor),
      backgroundColor: colorOrNull(captions.backgroundColor, base.backgroundColor),
      size: oneOf(CAPTION_SIZES, captions.size, base.size),
      position: oneOf(CAPTION_POSITIONS, captions.position, base.position),
      animation: animationOr(captions.animation, base.animation),
      uppercase: typeof captions.uppercase === 'boolean' ? captions.uppercase : base.uppercase,
    },
  };
}

const knownOnly = <T extends string>(allowed: T[], values: string[] | undefined, fallback: T[]): T[] =>
  Array.isArray(values) ? allowed.filter((value) => values.includes(value)) : fallback;

const boolOr = (value: unknown, fallback: boolean) => (typeof value === 'boolean' ? value : fallback);

export function mapCaptionCapabilities(data: CaptionCapabilitiesResponse | null | undefined): CaptionCapabilities {
  const base = DEFAULT_CAPTION_CAPABILITIES;
  const captions = data?.captions ?? {};
  const logo = data?.logo ?? {};
  return {
    captions: {
      fonts: knownOnly(CAPTION_FONTS, captions.fonts, base.captions.fonts),
      animations: knownOnly(CAPTION_ANIMATIONS, captions.animations, base.captions.animations),
      positions: knownOnly(CAPTION_POSITIONS, captions.positions, base.captions.positions),
      background: boolOr(captions.background, base.captions.background),
      layoutAlwaysDrawn: boolOr(captions.layoutAlwaysDrawn, base.captions.layoutAlwaysDrawn),
      uppercaseAnimations: knownOnly(
        CAPTION_ANIMATIONS,
        captions.uppercaseAnimations,
        base.captions.uppercaseAnimations
      ),
      timingEditable: boolOr(captions.timingEditable, base.captions.timingEditable),
    },
    logo: {
      placement: typeof logo.placement === 'string' ? logo.placement : base.logo.placement,
      position: boolOr(logo.position, base.logo.position),
      size: boolOr(logo.size, base.logo.size),
    },
    tags: { effect: typeof data?.tags?.effect === 'string' ? data.tags.effect : base.tags.effect },
  };
}

/** Uppercase is drawn only with some animations (`pop` today). */
export const uppercaseAllowed = (style: CaptionStyle, capabilities: CaptionCapabilities): boolean =>
  !!style.captions.animation && capabilities.captions.uppercaseAnimations.includes(style.captions.animation);

/** `value` when PPS can draw it, else `preferred`, else the first option it can draw. */
const drawable = <T extends string>(allowed: T[], value: T, preferred: T): T => {
  if (!allowed.length || allowed.includes(value)) return value;
  return allowed.includes(preferred) ? preferred : allowed[0];
};

/**
 * The nearest style PPS can draw: a font, position or animation it cannot draw moves to the default (or the first it
 * can), the background box goes when there is none, and uppercase goes without an animation that draws it. Logo
 * corner and size are kept as saved; the server keeps them for later.
 */
export function fitStyleToCapabilities(style: CaptionStyle, capabilities: CaptionCapabilities): CaptionStyle {
  const { captions } = capabilities;
  const defaults = DEFAULT_CAPTION_STYLE.captions;
  const animation =
    style.captions.animation && !captions.animations.includes(style.captions.animation)
      ? (captions.animations[0] ?? null)
      : style.captions.animation;
  const fitted: CaptionStyle = {
    ...style,
    captions: {
      ...style.captions,
      font: drawable(captions.fonts, style.captions.font, defaults.font),
      position: drawable(captions.positions, style.captions.position, defaults.position),
      backgroundColor: captions.background ? style.captions.backgroundColor : null,
      animation,
    },
  };
  return {
    ...fitted,
    captions: { ...fitted.captions, uppercase: fitted.captions.uppercase && uppercaseAllowed(fitted, capabilities) },
  };
}

/** The style body of a re-render: camelCase, without the logo image. */
export function toCaptionStylePayload(style: CaptionStyle) {
  return {
    captions: {
      font: style.captions.font,
      color: style.captions.color,
      highlightColor: style.captions.highlightColor,
      backgroundColor: style.captions.backgroundColor,
      size: style.captions.size,
      position: style.captions.position,
      animation: style.captions.animation,
      uppercase: style.captions.uppercase,
    },
    logo: {
      enabled: style.logo.enabled,
      position: style.logo.position,
      size: style.logo.size,
    },
  };
}

export const isValidStyleColor = (value: string): boolean => HEX_COLOR.test(value);

export function stylesAreEqual(a: CaptionStyle, b: CaptionStyle): boolean {
  return JSON.stringify(toCaptionStylePayload(a)) === JSON.stringify(toCaptionStylePayload(b));
}

// server_pray's captionFontFamilyFor (#19207), copied exactly: a reel without a chosen family is drawn in this one.
const DISPLAY_FONT = new RegExp(
  '^(?!.*sans).*(serif|display|playfair|abril|lobster|pacifico|merriweather|lora|' +
    'cinzel|bodoni|garamond|georgia|times|caslon|baskerville|script|slab)',
  'i'
);

// Read in this order: "Roboto Mono" is mono and "Roboto Condensed" condensed, though both are Roboto.
const FONT_FAMILY_HINTS: Array<[RegExp, CaptionFont]> = [
  [/mono|code|courier|consol/i, 'mono'],
  [/condensed|narrow|compressed|bebas|oswald|anton|league gothic|fjalla|teko|impact|knockout/i, 'condensed'],
  [DISPLAY_FONT, 'display'],
];

/** The family the renderer draws for a brand kit caption font, as server_pray picks it. */
export const fontFamilyOf = (fontName: string | null | undefined): CaptionFont =>
  FONT_FAMILY_HINTS.find(([pattern]) => pattern.test(fontName ?? ''))?.[1] ?? 'sans';

const isBrandKitPosition = (position: unknown): position is BrandKitCaptionPosition =>
  BRAND_KIT_CAPTION_POSITIONS.includes(position as BrandKitCaptionPosition);

/** The capabilities the brand kit's Captions group offers: the renderer's, less any position the kit can't hold. */
export function brandKitCaptionCapabilities(capabilities: CaptionCapabilities): CaptionCapabilities {
  return {
    ...capabilities,
    captions: { ...capabilities.captions, positions: capabilities.captions.positions.filter(isBrandKitPosition) },
  };
}

type BrandKitCaptionSource = Partial<
  Pick<
    BrandKitFields,
    | 'logo_url'
    | 'caption_color'
    | 'highlight_color'
    | 'caption_font_name'
    | 'caption_font_family'
    | 'caption_size'
    | 'caption_position'
    | 'caption_animation'
    | 'caption_uppercase'
    | 'logo_on_reels'
  >
>;

/**
 * The caption style the brand kit describes: its caption and highlight colours, its caption font (as the closest
 * family unless one was chosen), its logo (on unless turned off), and the caption fields. Unset fields keep the
 * renderer's defaults.
 */
export function captionStyleFromBrandKit(kit: BrandKitCaptionSource | null | undefined): CaptionStyle {
  const base = DEFAULT_CAPTION_STYLE;
  const position = kit?.caption_position;
  const color = (value: string | null | undefined) => (value && HEX_COLOR.test(value) ? value.toUpperCase() : null);
  return {
    logo: {
      ...base.logo,
      url: kit?.logo_url || null,
      enabled: !!kit?.logo_url && kit?.logo_on_reels !== false,
    },
    captions: {
      ...base.captions,
      font: oneOf(CAPTION_FONTS, kit?.caption_font_family, fontFamilyOf(kit?.caption_font_name)),
      color: color(kit?.caption_color),
      highlightColor: color(kit?.highlight_color),
      size: oneOf(CAPTION_SIZES, kit?.caption_size, base.captions.size),
      position: isBrandKitPosition(position) ? position : base.captions.position,
      animation: animationOr(kit?.caption_animation, base.captions.animation),
      uppercase: kit?.caption_uppercase ?? base.captions.uppercase,
    },
  };
}

/**
 * The brand kit fields a caption style edit writes (colours included: they are the kit's caption colours). A
 * position the kit can't hold is written as null, the renderer's default.
 */
export function brandKitCaptionFields(
  style: CaptionStyle
): Required<BrandKitCaptionStyleFields> & Pick<BrandKitFields, 'caption_color' | 'highlight_color'> {
  return {
    caption_color: style.captions.color,
    highlight_color: style.captions.highlightColor,
    caption_font_family: style.captions.font,
    caption_size: style.captions.size,
    caption_position: isBrandKitPosition(style.captions.position) ? style.captions.position : null,
    caption_animation: style.captions.animation,
    caption_uppercase: style.captions.uppercase,
    logo_on_reels: style.logo.enabled,
  };
}
